import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Select, Switch, Tag, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../../helpers/API/amplifyApi";
import LoadableComp from "../../../commonComponents/LoadableComp/LoadableComp";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import {
  SEASONAL_THEMES_FIELD_ID,
  SEASONAL_THEMES_FIELD_NAME,
  THEME_LABELS,
  buildDefaultSeasonalThemesOptions,
} from "../../../../constantsFactory/seasonalThemes";
import {
  normalizeSeasonalThemesOptions,
  resolveActiveSeasonalTheme,
  findSeasonalThemesField,
} from "../../../../utils/seasonalTheme";
import { programFields as programFieldsAction } from "../../../../store/actions";
import ThemeScheduleRow from "./ThemeScheduleRow";
import "./TemaSezonale.scss";

async function ensureSeasonalField() {
  try {
    const existing = await API.get(
      "programFields",
      `/programFields/${SEASONAL_THEMES_FIELD_ID}`,
    );
    if (existing?.fieldId) return existing;
  } catch {
    /* create below */
  }

  const created = await API.post("programFields", "/programFields", {
    body: {
      fieldId: SEASONAL_THEMES_FIELD_ID,
      fieldName: SEASONAL_THEMES_FIELD_NAME,
      fieldOptions: buildDefaultSeasonalThemesOptions(),
    },
  });
  return created;
}

function TemaSezonale() {
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector((state) => state.usersList);
  const programFieldsList = useSelector(
    (state) => state.programFields?.programFields,
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(buildDefaultSeasonalThemesOptions());
  /** Theme selected for manual Test / Activate controls */
  const [manualThemeId, setManualThemeId] = useState("halloween");

  const active = useMemo(
    () => resolveActiveSeasonalTheme(options),
    [options],
  );

  const syncRedux = useCallback(
    (fieldOptions) => {
      const list = Array.isArray(programFieldsList)
        ? [...programFieldsList]
        : [];
      const idx = list.findIndex(
        (f) => f?.fieldId === SEASONAL_THEMES_FIELD_ID,
      );
      const row = {
        fieldId: SEASONAL_THEMES_FIELD_ID,
        fieldName: SEASONAL_THEMES_FIELD_NAME,
        fieldOptions,
      };
      if (idx >= 0) list[idx] = { ...list[idx], ...row };
      else list.push(row);
      dispatch(programFieldsAction(list));
    },
    [dispatch, programFieldsList],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fromRedux = findSeasonalThemesField(programFieldsList);
      if (fromRedux?.fieldOptions) {
        setOptions(normalizeSeasonalThemesOptions(fromRedux.fieldOptions));
      }
      const field = await ensureSeasonalField();
      const next = normalizeSeasonalThemesOptions(field?.fieldOptions);
      setOptions(next);
      if (next.forceThemeId) setManualThemeId(next.forceThemeId);
      syncRedux(next);
    } catch (err) {
      console.error(err);
      message.error("Nuk u ngarkua dot konfigurimi i temave sezonale.");
    } finally {
      setLoading(false);
    }
  }, [programFieldsList, syncRedux]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (nextOptions, successText) => {
    setSaving(true);
    try {
      await ensureSeasonalField();
      await API.put(
        "programFields",
        `/programFields/${SEASONAL_THEMES_FIELD_ID}`,
        { body: { fieldOptions: nextOptions } },
      );
      setOptions(nextOptions);
      syncRedux(nextOptions);
      message.success(successText || "Temat sezonale u ruajtën.");
      return true;
    } catch (err) {
      console.error(err);
      message.error("Ndodhi një gabim gjatë ruajtjes.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onThemeChange = (updated) => {
    setOptions((prev) => ({
      ...prev,
      themes: prev.themes.map((t) => (t.id === updated.id ? updated : t)),
    }));
  };

  /** Manual OFF — company-wide, clears force + master */
  const onTurnOffNow = () => {
    const next = {
      ...options,
      enabled: false,
      forceThemeId: null,
    };
    save(next, "Tema u fik. Të gjithë përdoruesit e shohin UI-në normale.");
  };

  /**
   * Manual ON / Test — force a pack for everyone immediately
   * (saved to DynamoDB programFields).
   */
  const onActivateNow = (themeId = manualThemeId) => {
    if (!themeId) {
      message.warning("Zgjidh një temë për të aktivizuar.");
      return;
    }
    const label = THEME_LABELS[themeId] || themeId;
    const next = {
      ...options,
      enabled: true,
      forceThemeId: themeId,
    };
    save(
      next,
      `${label} u aktivizua tani për të gjithë (ruajtur në DynamoDB).`,
    );
  };

  /** Same as activate, wording for quick QA */
  const onTestNow = () => onActivateNow(manualThemeId);

  return (
    <LoadableComp loading={loading}>
      <div
        className={`tema-sezonale globalPageWrapper ${isDarkMode ? "dark" : ""}`}
      >
        <div className="tema-sezonale__header">
          <div>
            <h1 className="tema-sezonale__title">Tema sezonale</h1>
            <p className="tema-sezonale__subtitle">
              Planifiko Verën, Halloween, Vjeshtën, Thanksgiving, Ditën e
              Flamurit dhe Dimrin me kalendar — ose ndiz / fik manualisht
              tani për të gjithë kompaninë.
            </p>
          </div>
          <div className="tema-sezonale__status">
            <span>Aktive tani:</span>
            {active ? (
              <Tag color="green">{active.label}</Tag>
            ) : (
              <Tag>Asnjë</Tag>
            )}
            {active?.forced ? <Tag color="blue">Manual / Test</Tag> : null}
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          message="Ruajtja"
          description="Çdo buton «Testo / Aktivizo / Fik» shkruan menjëherë në DynamoDB. Pas aktivizimit: kliko gjethet/lakuriqët/borën për efekte; te Dimri kliko llambat e Krishtlindjes; te Halloween kliko fantazmën. Orari ruhet me «Ruaj orarin»."
        />

        {/* Manual on / off / test */}
        <div className="tema-sezonale__card tema-sezonale__manual">
          <div className="tema-sezonale__master-label">Kontroll manual</div>
          <div className="tema-sezonale__master-hint">
            Ndiz ose fik temën menjëherë për të gjithë — pa pritur datën e
            kalendarit.
          </div>

          <div className="tema-sezonale__manual-row">
            <div className="tema-sezonale__manual-select">
              <label className="tema-sezonale__label">Tema për test / aktivizim</label>
              <Select
                style={{ minWidth: 240, width: "100%" }}
                value={manualThemeId}
                onChange={setManualThemeId}
                options={Object.entries(THEME_LABELS).map(([id, label]) => ({
                  value: id,
                  label,
                }))}
              />
            </div>

            <div className="tema-sezonale__manual-btns">
              <MondayButton
                className="mondayButtonBlue"
                onClick={onTestNow}
                disabled={saving || !manualThemeId}
                spin={saving}
              >
                Testo tani
              </MondayButton>
              <MondayButton
                className="mondayButtonGreen"
                onClick={() => onActivateNow(manualThemeId)}
                disabled={saving || !manualThemeId}
              >
                Aktivizo tani
              </MondayButton>
              <MondayButton
                className="mondayButtonRed"
                onClick={onTurnOffNow}
                disabled={saving}
              >
                Fik tani
              </MondayButton>
            </div>
          </div>

          <div className="tema-sezonale__manual-state">
            Gjendja e ruajtur: master{" "}
            <Tag color={options.enabled ? "green" : "default"}>
              {options.enabled ? "ON" : "OFF"}
            </Tag>
            {options.forceThemeId ? (
              <>
                {" "}
                · force{" "}
                <Tag color="blue">
                  {THEME_LABELS[options.forceThemeId] || options.forceThemeId}
                </Tag>
              </>
            ) : (
              <> · force <Tag>asnjë</Tag></>
            )}
          </div>
        </div>

        <div className="tema-sezonale__card">
          <div className="tema-sezonale__master">
            <div>
              <div className="tema-sezonale__master-label">
                Master (kalendar automatik)
              </div>
              <div className="tema-sezonale__master-hint">
                ON = lejon kalendarin (dhe force). OFF = asnjë temë, edhe nëse
                datat përputhen. Ndryshimi ruhet me «Ruaj orarin» ose me
                butonat manualë.
              </div>
            </div>
            <Switch
              checked={!!options.enabled}
              onChange={(checked) =>
                setOptions((prev) => ({ ...prev, enabled: checked }))
              }
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>
        </div>

        <div className="tema-sezonale__card">
          <div className="tema-sezonale__master-label" style={{ marginBottom: 12 }}>
            Orari i kalendarit
          </div>
          {options.themes.map((theme) => (
            <ThemeScheduleRow
              key={theme.id}
              theme={theme}
              isDarkMode={isDarkMode}
              isActive={active?.id === theme.id}
              onChange={onThemeChange}
            />
          ))}
        </div>

        <div className="tema-sezonale__actions">
          <MondayButton
            className="mondayButtonGreen"
            spin={saving}
            onClick={() =>
              save(options, "Orari i temave u ruajt në DynamoDB.")
            }
          >
            Ruaj orarin
          </MondayButton>
        </div>
      </div>
    </LoadableComp>
  );
}

export default TemaSezonale;
